import React, { ComponentProps, useCallback, useState } from 'react';

import fileDownload from 'react-file-download';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useDownloadManifest } from '~/hooks/apis/tours/useDownloadManifest';

const TRIGGERED_DOWNLOAD_HINT =
  'File is encrypted. Use the password below to open the archive. The password is only valid for this file and should not be shared externally.';

export default function ToursManifestPage() {
  const [tourId, setTourId] = useState<string>('');
  const [departureName, setDepartureName] = useState<string>('');
  const [password, setPassword] = useState<string | null>(null);
  const [downloadedFilename, setDownloadedFilename] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [copiedPassword, setCopiedPassword] = useState<boolean>(false);

  const downloadMutation = useDownloadManifest();

  const resetResultState = useCallback(() => {
    setPassword(null);
    setDownloadedFilename(null);
    setShowPassword(false);
    setCopiedPassword(false);
  }, []);

  const handleSubmit = useCallback<NonNullable<ComponentProps<'form'>['onSubmit']>>(
    async (event) => {
      event.preventDefault();

      const trimmedTourId = tourId.trim();
      const trimmedDepartureName = departureName.trim();

      if (!trimmedTourId || !trimmedDepartureName) {
        return;
      }

      resetResultState();

      try {
        const result = await downloadMutation.mutateAsync({
          tourId: trimmedTourId,
          departureName: trimmedDepartureName,
        });

        fileDownload(result.blob, result.filename);
        setDownloadedFilename(result.filename);
        setPassword(result.password);
      } catch {
        // Error state is surfaced via downloadMutation.error below.
      }
    },
    [tourId, departureName, downloadMutation, resetResultState],
  );

  const handleCopyPassword = useCallback(async () => {
    if (!password) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = password;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedPassword(true);
      window.setTimeout(() => setCopiedPassword(false), 2000);
    } catch (err) {
      console.error('Failed to copy manifest password:', err);
    }
  }, [password]);

  const canSubmit = tourId.trim().length > 0 && departureName.trim().length > 0 && !downloadMutation.isPending;

  const errorMessage =
    downloadMutation.isError && downloadMutation.error instanceof Error
      ? downloadMutation.error.message
      : downloadMutation.isError
      ? 'Failed to download manifest.'
      : null;

  return (
    <Container maxWidth="lg">
      <PageHeader title="Departure Manifests" subtitle="Download the departure manifest for a tour deal" />

      <Paper variant="outlined" sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Opportunity ID"
                  placeholder="e.g. 006RF00000UzMn7YAF"
                  value={tourId}
                  onChange={(event) => setTourId(event.target.value)}
                  fullWidth
                  required
                  autoFocus
                  disabled={downloadMutation.isPending}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Departure name"
                  placeholder="e.g. SD-0019083"
                  value={departureName}
                  onChange={(event) => setDepartureName(event.target.value)}
                  fullWidth
                  required
                  disabled={downloadMutation.isPending}
                />
              </Grid>
            </Grid>

            <Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!canSubmit}
                  startIcon={
                    downloadMutation.isPending ? <CircularProgress color="inherit" size={16} /> : <DownloadIcon />
                  }
                >
                  {downloadMutation.isPending ? 'Downloading…' : 'Download manifest'}
                </Button>
              </Stack>
            </Box>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            {password && downloadedFilename && (
              <Alert severity="success" icon={false}>
                <Stack spacing={2}>
                  <Typography variant="body2">
                    Downloaded <strong>{downloadedFilename}</strong>. {TRIGGERED_DOWNLOAD_HINT}
                  </Typography>

                  <TextField
                    label="Password"
                    value={password}
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                              <IconButton
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                onClick={() => setShowPassword((prev) => !prev)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={copiedPassword ? 'Copied!' : 'Copy password'}>
                              <IconButton aria-label="Copy password" onClick={handleCopyPassword} edge="end">
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>
              </Alert>
            )}
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
